import React, { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { StatCard, COLORS } from '../charts';
import { runLedger, parseInputs, encodeInputs, INPUT_SPEC, DEFAULTS, usdM, pctText } from '../../data/rcmLedger';
import '../portfolio/portfolio.css';
import './rcm.css';

// The teaser's ledger, live: the platform's scale and today's benchmark
// rates on the left, the arithmetic on the right. Inputs live in the URL
// (?encounters=3000000&netPerVisit=167) so a tour step carries a scenario.
// Frames: the first 90 days · the program at run-rate · the total.
const FRAMES = [
  { id: 'first', label: 'The first 90 days', note: 'One payer slice of one row — the piece that ships in weeks and proves the method.' },
  { id: 'program', label: 'The program, at run-rate', note: 'Benchmark × scale. Headroom is the ceiling the whole operation could reach; it is not added to anything.' },
  { id: 'total', label: 'The total, 36 months', note: 'Run-rate savings from the month the program is live, plus the A/R cash released once. Gross; platform and build are priced in the proposal.' },
];
const GROUPS = [...new Set(INPUT_SPEC.map((s) => s.group))];
const fmtIn = (v) => (Math.abs(v) >= 1e6 ? (v / 1e6).toFixed(1) + 'M' : String(v));

export default function Ledger({ frame = null }) {
  const [params, setParams] = useSearchParams();
  const inputs = useMemo(() => parseInputs(params), [params]);
  const r = useMemo(() => runLedger(inputs), [inputs]);
  const [i, setI] = useState(() => (frame != null ? Math.min(Math.max(0, frame), FRAMES.length - 1) : 0));
  useEffect(() => { if (frame != null) setI(Math.min(Math.max(0, frame), FRAMES.length - 1)); }, [frame]);

  const set = (key, raw) => setParams(encodeInputs({ ...inputs, [key]: raw === '' ? DEFAULTS[key] : Number(raw) }, params), { replace: true });
  const reset = () => setParams(encodeInputs(DEFAULTS, params), { replace: true });
  const dirty = INPUT_SPEC.some((s) => inputs[s.key] !== DEFAULTS[s.key]);
  const tab = FRAMES[i];
  const In = inputs;

  return (
    <div className="as-screen pf-model rcm-ledger" data-nova-note="The teaser ledger as a calculator. Left: the platform's scale and today's benchmark rates (editable; the URL carries the scenario). Right: the arithmetic for the first 90 days, the program at run-rate, and the 36-month total. Headroom is a ceiling, never added.">
      <div className="as-tabs" role="tablist">
        {FRAMES.map((t, k) => (
          <button key={t.id} type="button" role="tab" aria-selected={k === i} className={k === i ? 'on' : ''} onClick={() => setI(k)}>{t.label}</button>
        ))}
        <span className="as-tabctl" role="group" aria-label="frame controls">
          <button type="button" onClick={() => setI((i + FRAMES.length - 1) % FRAMES.length)} aria-label="previous frame">‹</button>
          <button type="button" onClick={() => setI((i + 1) % FRAMES.length)} aria-label="next frame">›</button>
          <small>{i + 1} / {FRAMES.length}</small>
        </span>
      </div>

      <div className="pf-grid">
        <aside className="pf-inputs" aria-label="Ledger inputs">
          <div className="pf-inputs-head">
            <strong>Inputs</strong>
            {dirty && <button type="button" className="cs-btn cs-btn-sm cs-btn-outline" onClick={reset}>reset</button>}
          </div>
          {GROUPS.map((g) => (
            <div key={g} className="pf-group">
              <p className="pf-group-label">{g}</p>
              {INPUT_SPEC.filter((s) => s.group === g).map((s) => (
                <label key={s.key} className={`pf-input ${inputs[s.key] !== DEFAULTS[s.key] ? 'is-changed' : ''}`}>
                  <span>{s.label}</span>
                  <input type="number" inputMode="decimal" value={inputs[s.key]} min={s.range[0]} max={s.range[1]} step={s.range[2]} onChange={(e) => set(s.key, e.target.value)} />
                  <small>{s.unit}</small>
                </label>
              ))}
            </div>
          ))}
          <p className="pf-note rcm-npr">≈ <b>{usdM(r.npr)}</b> net patient revenue · <b>{Math.round(r.denials).toLocaleString()}</b> denials a year</p>
        </aside>

        <div className="pf-pane">
          {tab.id === 'first' && (
            <>
              <div className="pf-stats">
                <StatCard label="First initiative, per year" display={usdM(r.first.usd)} color={COLORS.amber} />
                <StatCard label="As % of net revenue" display={pctText(r.first.pct)} color={COLORS.amber} />
                <StatCard label="By month 36" display={usdM(r.first.by36)} />
                <StatCard label="Share of the rework row" display={pctText(r.rework.firstIsShareOf, 0)} />
              </div>
              <ol className="rcm-math">
                <li><span>Denials a year</span><em>{fmtIn(In.encounters)} encounters × {In.denialRate}% = <b>{Math.round(r.denials).toLocaleString()}</b></em></li>
                <li><span>The slice the first initiative touches</span><em>top-3 payers {In.payerShare}% × born at registration {In.frontEndShare}% × prevented at intake {In.prevented}% = <b>{pctText(r.first.fewerShare, 0)} fewer denials</b></em></li>
                <li><span>Rework labor not spent</span><em>{Math.round(r.denials * r.first.fewerShare).toLocaleString()} × ${In.reworkCost} = <b>{usdM(r.first.usd)} a year</b></em></li>
                <li><span>Over the three years</span><em>× 3 = <b>{usdM(r.first.by36)}</b> — it ships in weeks, so it counts from year one</em></li>
              </ol>
            </>
          )}
          {tab.id === 'program' && (
            <>
              <div className="pf-stats">
                <StatCard label="Rework labor on denials" display={`${usdM(r.rework.usd)} / yr`} color={COLORS.teal} />
                <StatCard label="Days in A/R, cash once" display={usdM(r.ar.usd)} color={COLORS.teal} />
                <StatCard label="Cost to collect — headroom" display={`${usdM(r.ctc.headroom)} / yr`} color={COLORS.amber} />
                <StatCard label="Rework, % of NPR" display={pctText(r.rework.pct)} />
              </div>
              <ol className="rcm-math">
                <li><span>Rework spend today</span><em>{Math.round(r.denials).toLocaleString()} denials × ${In.reworkCost} = <b>{usdM(r.rework.spend)} a year</b></em></li>
                <li><span>Halve the gap to the top decile</span><em>{In.denialRate}% → {In.targetDenial}% = <b>{pctText(r.rework.fewerShare, 0)} fewer denials</b> → <b>{usdM(r.rework.usd)}</b> a year · the first initiative is {pctText(r.rework.firstIsShareOf, 0)} of it; triage on every payer plus the prevention levers take the rest</em></li>
                <li><span>Days in A/R</span><em>{In.arDays} → {In.arTarget} days × {usdM(r.ar.perDay)} of revenue a day = <b>{usdM(r.ar.usd)} cash, once</b> ({pctText(r.ar.pct)} of NPR)</em></li>
                <li className="is-amber"><span>Cost to collect — where it sits</span><em>{In.costToCollect}% vs the top decile’s {In.topDecileCtc}% = <b>{usdM(r.ctc.headroom)} a year of headroom</b> ({pctText(r.ctc.pct)}). Rework is the first point of it; reauth, intake and rule watch take the next. A ceiling — not added to the rows above.</em></li>
              </ol>
            </>
          )}
          {tab.id === 'total' && (
            <>
              <div className="pf-stats">
                <StatCard label="Program total, 36 months" display={usdM(r.total.usd)} color={COLORS.teal} />
                <StatCard label="Of three-year net revenue" display={pctText(r.total.pctOf3yr)} color={COLORS.teal} />
                <StatCard label="Run-rate years counted" display={r.total.runRateYears.toFixed(1)} />
                <StatCard label="Headroom not counted" display={`${usdM(r.ctc.headroom)} / yr`} color={COLORS.amber} />
              </div>
              <ol className="rcm-math">
                <li><span>Run-rate savings</span><em>{usdM(r.rework.usd)} a year from month {In.rampMonths + 1} = {r.total.runRateYears.toFixed(1)} years = <b>{usdM(r.rework.usd * r.total.runRateYears)}</b></em></li>
                <li><span>Cash released once</span><em>+ <b>{usdM(r.ar.usd)}</b> from days in A/R</em></li>
                <li><span>Program total</span><em>= <b>{usdM(r.total.usd)} gross</b> · {pctText(r.total.pctOf3yr)} of three-year net revenue</em></li>
                <li className="is-amber"><span>Not in the total</span><em>the {usdM(r.ctc.headroom)}/yr cost-to-collect headroom (the ceiling), prior-auth write-offs, appeal recoveries, onboarding days — each counted on the platform’s own records once live</em></li>
              </ol>
            </>
          )}
          <p className="pf-note">{tab.note}</p>
        </div>
      </div>
      <div className="as-foot">
        <span className="as-foot-chip">benchmark × scale</span>
        <span>Illustrative and gross. ABA / Kodiak 2025–26 (11.8% denials, 42 days A/R, 9.4% cost to collect), Premier 2024 ($43.84 rework). The platform’s own rates replace these on day one.</span>
      </div>
    </div>
  );
}
